import React, { useState, useEffect, useCallback } from 'react'
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { QrCode, Copy, CheckCircle, AlertCircle, Loader2, CreditCard } from 'lucide-react'
import { Payment } from '@mercadopago/sdk-react'
import { MERCADOPAGO_CONFIG } from '@/config/mercadopago'
import { useSearchParams } from 'react-router-dom'

const VALOR = MERCADOPAGO_CONFIG.PAYMENT_CONFIG.DEFAULT_PRICE
const PIX_POLL_INTERVAL = 3000
const PIX_EXPIRATION_MS = MERCADOPAGO_CONFIG.PAYMENT_CONFIG.PIX_EXPIRATION_MINUTES * 60 * 1000

const PAYMENT_INITIALIZATION = { amount: VALOR }
const PAYMENT_CUSTOMIZATION = {
  paymentMethods: {
    creditCard: 'all',
    maxInstallments: 12,
  },
  visual: {
    defaultPaymentOption: { creditCardForm: true },
  },
}

/** Mensagem amigável quando a API retorna HTML (ex.: 500 do Laravel) ou erro do Mercado Pago */
function getMensagemAmigavelErroPagamento(status, responseText, data) {
  const texto = (responseText || '').toLowerCase()
  const isHtml = texto.trimStart().startsWith('<!') || texto.includes('</html>')

  if (data?.message && typeof data.message === 'string' && !data.message.trimStart().toLowerCase().startsWith('<!')) {
    const msg = data.message.trim()
    if (msg.length < 500) return msg
  }
  if (data?.error && typeof data.error === 'string' && data.error.length < 500) return data.error

  if (status === 404) {
    return 'API não encontrada. Verifique se VITE_API_URL aponta para o ca-api e se o backend está rodando.'
  }

  if (status >= 500 || isHtml) {
    if (texto.includes('payer.email') || texto.includes('4050') || texto.includes('payer.email')) {
      return 'E-mail do paciente inválido ou não cadastrado na cirurgia. Corrija o e-mail no formulário da cirurgia e tente novamente.'
    }
    return 'Erro temporário no processamento do pagamento. Tente novamente em alguns instantes.'
  }

  return `Erro ${status}. Tente novamente.`
}

function CardPaymentBrick({ onSubmit, onError }) {
  return (
    <Payment
      id="paymentBrick_container"
      initialization={PAYMENT_INITIALIZATION}
      customization={PAYMENT_CUSTOMIZATION}
      locale="pt-BR"
      onSubmit={onSubmit}
      onError={onError}
    />
  )
}

const PremiumCheckout = ({ onSuccess, onError }) => {
  const [searchParams] = useSearchParams()
  const cirurgiaId = searchParams.get('cirurgiaId')

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [pixData, setPixData] = useState(null)
  const [copied, setCopied] = useState(false)
  const [cpf, setCpf] = useState('')
  const [activeTab, setActiveTab] = useState('pix')

  const formatCpf = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11)
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
  }

  const handleCpfChange = (e) => {
    setCpf(formatCpf(e.target.value))
  }

  const getCpfNumerico = () => cpf.replace(/\D/g, '')

  const isValidCpf = () => getCpfNumerico().length === 11

  const iniciarPagamentoPix = async () => {
    if (!cirurgiaId) {
      setError('É necessário acessar esta página com o link enviado. O ID da cirurgia não foi encontrado.')
      return
    }
    if (!isValidCpf()) {
      setError('Digite um CPF válido (11 dígitos).')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const url = MERCADOPAGO_CONFIG.API_URLS.INICIAR_PAGAMENTO(cirurgiaId)
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          metodo: 'pix',
          valor: VALOR,
          cpf: getCpfNumerico(),
        }),
      })

      const text = await response.text()
      let data
      try {
        data = text ? JSON.parse(text) : {}
      } catch {
        data = {}
      }

      if (!response.ok) {
        const friendlyMessage = getMensagemAmigavelErroPagamento(response.status, text, data)
        throw new Error(friendlyMessage)
      }

      setPixData({
        qr_code: data.qr_code,
        qr_code_base64: data.qr_code_base64,
        expires_at: Date.now() + PIX_EXPIRATION_MS,
      })
    } catch (err) {
      setError(err.message || 'Erro ao processar pagamento PIX. Tente novamente.')
      onError?.(err)
    } finally {
      setLoading(false)
    }
  }

  const copyPixCode = async () => {
    if (pixData?.qr_code) {
      try {
        await navigator.clipboard.writeText(pixData.qr_code)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error('Erro ao copiar:', err)
      }
    }
  }

  // Poll status do PIX
  useEffect(() => {
    if (!pixData || !cirurgiaId) return

    const checkStatus = async () => {
      try {
        const url = MERCADOPAGO_CONFIG.API_URLS.STATUS_PAGAMENTO(cirurgiaId)
        const res = await fetch(url)
        const data = await res.json()
        if (data.status === 'approved') {
          onSuccess?.()
        }
      } catch (err) {
        console.error('Erro ao verificar status:', err)
      }
    }

    const interval = setInterval(checkStatus, PIX_POLL_INTERVAL)
    return () => clearInterval(interval)
  }, [pixData, cirurgiaId, onSuccess])

  const handleCartaoSubmit = useCallback(async (param) => {
    if (!cirurgiaId) {
      const msg = 'É necessário acessar esta página com o link enviado.'
      setError(msg)
      onError?.(new Error(msg))
      throw new Error(msg)
    }
    const formData = param?.formData || param
    const cpfPayer = formData?.payer?.identification?.number?.replace(/\D/g, '') || getCpfNumerico()
    if (!cpfPayer || cpfPayer.length !== 11) {
      const msg = 'CPF obrigatório. Preencha no formulário do cartão.'
      setError(msg)
      throw new Error(msg)
    }

    setLoading(true)
    setError(null)

    try {
      const url = MERCADOPAGO_CONFIG.API_URLS.INICIAR_PAGAMENTO(cirurgiaId)
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          metodo: 'cartao_credito',
          valor: VALOR,
          cpf: cpfPayer,
          token: formData.token,
          payment_method_id: formData.payment_method_id,
          installments: formData.installments || 1,
          issuer_id: formData.issuer_id || null,
        }),
      })

      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.message || data.error?.message || 'Erro ao processar cartão')
      }

      if (data.status === 'approved') {
        onSuccess?.()
        return
      }
      const msg = data.status === 'pending' || data.status === 'in_process'
        ? 'Pagamento em análise. Aguarde a confirmação.'
        : (data.status_detail || 'Pagamento não aprovado. Tente novamente.')
      setError(msg)
      throw new Error(msg)
    } catch (err) {
      const msg = err.message || 'Erro ao processar cartão. Tente novamente.'
      setError(msg)
      onError?.(err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [cirurgiaId, cpf])

  const handleCartaoError = useCallback((err) => {
    setError(err?.message || 'Erro no formulário do cartão')
    onError?.(err)
  }, [onError])

  const handleBack = () => {
    setPixData(null)
    setError(null)
  }

  if (!cirurgiaId) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="pt-8 pb-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Acesse esta página pelo link enviado para você. O ID da cirurgia é obrigatório para realizar o pagamento.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    )
  }

  if (pixData) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-green-600">Pagamento PIX</CardTitle>
          <p className="text-gray-600">Escaneie o QR Code ou copie o código PIX</p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            <div className="bg-gray-100 p-4 rounded-lg inline-block">
              {pixData.qr_code_base64 ? (
                <img
                  src={pixData.qr_code_base64.startsWith('data:') ? pixData.qr_code_base64 : `data:image/png;base64,${pixData.qr_code_base64}`}
                  alt="QR Code PIX"
                  className="w-48 h-48 mx-auto"
                />
              ) : (
                <div className="w-48 h-48 flex items-center justify-center text-gray-400">QR Code</div>
              )}
            </div>
          </div>
          {pixData.qr_code && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Código PIX</Label>
              <div className="flex items-center gap-2">
                <Input value={pixData.qr_code} readOnly className="text-xs font-mono" />
                <Button size="sm" variant="outline" onClick={copyPixCode} className="min-w-[60px]">
                  {copied ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              {copied && <p className="text-xs text-green-600">Código copiado!</p>}
            </div>
          )}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <strong>Instruções:</strong>
              <br />1. Abra o app do seu banco
              <br />2. Escolha PIX
              <br />3. Escaneie o QR Code ou cole o código
              <br />4. Confirme o pagamento
            </AlertDescription>
          </Alert>
          <div className="space-y-3">
            <Button onClick={handleBack} variant="outline" className="w-full">Voltar</Button>
            <Button onClick={() => onSuccess?.()} className="w-full bg-green-600 hover:bg-green-700">Já paguei</Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl font-bold text-green-600">Cirurgia Premium</CardTitle>
        <p className="text-gray-600">R$ {VALOR.toFixed(2).replace('.', ',')} — PIX ou Cartão de Crédito</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {activeTab === 'pix' && (
          <div className="space-y-2">
            <Label htmlFor="cpf">CPF *</Label>
            <Input
              id="cpf"
              value={cpf}
              onChange={handleCpfChange}
              placeholder="000.000.000-00"
              maxLength={14}
            />
            <p className="text-xs text-gray-500">Obrigatório para PIX.</p>
          </div>
        )}

        <div className="space-y-4">
          {activeTab === 'pix' ? (
            <>
              <Button
                onClick={iniciarPagamentoPix}
                disabled={loading || !isValidCpf()}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Gerando PIX...
                  </>
                ) : (
                  <>Pagar R$ {VALOR.toFixed(2).replace('.', ',')} com PIX</>
                )}
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setActiveTab('cartao')}
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  Usar Cartão de Crédito
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Pagamento com Cartão de Crédito</p>
                <div className="min-h-[320px] w-full">
                  <CardPaymentBrick
                    onSubmit={handleCartaoSubmit}
                    onError={handleCartaoError}
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                className="w-full text-gray-600"
                onClick={() => setActiveTab('pix')}
              >
                ← Voltar para PIX
              </Button>
            </>
          )}
        </div>

        <div className="bg-gray-50 p-4 rounded-lg text-sm">
          <p className="font-semibold text-gray-900">Resumo</p>
          <p className="text-gray-600">Cirurgia Premium — R$ {VALOR.toFixed(2).replace('.', ',')}</p>
          <p className="text-xs text-gray-500 mt-2">Pagamento seguro via Mercado Pago</p>
        </div>
      </CardContent>
    </Card>
  )
}

export default PremiumCheckout
